import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewEncapsulation
} from '@angular/core';
import {MatMenu, MatMenuTrigger} from '@angular/material/menu';
import {AppScope} from "../../services/app_scope.service";
import {NotificationService} from "../../services/notification.service";
import {TableUtilsService} from "../../services/table-utils.service";
import {COMMON_FORM_IMPORTS} from "../../shared/import-groups/Common";
import {ColorPickerModule} from "ngx-color-picker";
import {NOTIFICATION_DURATION_MS} from "../../shared/globals";
import { UpperFirstPipe } from '../../shared/pipes';
import {CommonModule} from "@angular/common";

export interface IColumnFormatsConfig {
    bold: boolean;
    italic: boolean;
    abbreviate: boolean;
    size: number;
    resize: boolean;
    fit_content: boolean;
    width: number | 'auto';
    height: number | 'auto';
    colour: string;
    background_colour: string;
    underline: boolean;
    strikethrough: boolean;
    aggregation_type: string;
    align: string;
    decimals: number;
    title: string;
}

export type ColumnFormatsConfig = Partial<IColumnFormatsConfig>;
export type ColumnFormatsConfigDict = Record<string, Partial<IColumnFormatsConfig>>;

@Component({
    selector: 'table-column-menu',
    templateUrl: './table-column-menu.component.html',
    styleUrls: ['./table-column-menu.component.less'],
    encapsulation: ViewEncapsulation.None,
    imports: [
        CommonModule,
        COMMON_FORM_IMPORTS,
        MatMenuTrigger,
        MatMenu,
        ColorPickerModule,
        UpperFirstPipe
    ],
})
export class TableColumnMenuComponent implements OnInit, OnChanges {
    @Input() item: ColumnFormatsConfig;
    @Input() options: string[] = ['bold', 'italic', 'abbreviate', 'size', 'resize', 'width', 'colour', 'align']; // Defaults
    @Output() formatsChanged: EventEmitter<ColumnFormatsConfig> = new EventEmitter();
    @Input() default_values: ColumnFormatsConfigDict;
    @Input() dataType: string; // Add data type input
    @Input() aggregationTypes = ['default', 'total', 'average', 'min', 'max', 'count', 'none'];
    preset_colours: string[];
    rows: Record<number, boolean> = {1: false, 2: false, 3: false};

    formats: ColumnFormatsConfig;

    constructor(private notification: NotificationService,
                private appScope: AppScope,
                private tableUtils: TableUtilsService) {
    }

    showNestedMenu(menu: MatMenuTrigger) {
        menu.openMenu();
    }

    ngOnInit(): void {
        if (!this.item || !Object.keys(this.item).length) {
            this.item = this.defaultConfig();
            this.formatChanged();
        }

        // Set default aggregation type if not specified
        if (this.options.includes('aggregations') && !this.item.aggregation_type) {
            // Default to 'default' for numeric types, 'none' for others
            if (this.dataType === 'float' || this.dataType === 'number') {
                this.item.aggregation_type = 'default';
            } else {
                this.item.aggregation_type = 'none';
            }
        }

        this.preset_colours = this.appScope.config_name_map?.palette1?.value?.map(item => item.colour) || [];

        this.rows[1] = this.options.some(x => ['bold', 'italic', 'fit_content', 'size', 'resize', 'width', 'colour', 'background_colour',
            'underline', 'strikethrough', 'aggregations', 'decimals'].includes(x));
        this.rows[2] = this.options.some(x => ['align', 'size', 'abbreviate'].includes(x));

        this.updateAggregationTypes();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (!this.item) {
            this.item = this.defaultConfig();
            this.formatChanged();
        }
        if (changes['dataType']) {
            this.updateAggregationTypes();
        }
    }

    updateAggregationTypes(): void {
        // Set aggregation types based on data type
        switch (this.dataType) {
            case 'string':
            case 'user':
            case 'user_group':
            case 'account':
            case 'file':
                // String-like types only support count, none
                this.aggregationTypes = ['count', 'none'];
                break;
            case 'datetime':
                // DateTime supports count, min, max, none
                this.aggregationTypes = ['count', 'min', 'max', 'none'];
                break;
            case 'float':
            case 'number':
                // Float/number supports all aggregation types except none
                this.aggregationTypes = ['default', 'total', 'average', 'min', 'max', 'count'];
                break;
            default:
                // Default to all types if data type is unknown
                this.aggregationTypes = ['default', 'total', 'average', 'min', 'max', 'count', 'none'];
                break;
        }

        // Ensure appropriate default is selected if current aggregation type is not available
        if (this.item?.aggregation_type && !this.aggregationTypes.includes(this.item.aggregation_type)) {
            // Reset to 'default' for numeric types, 'none' for others
            if (this.dataType === 'float' || this.dataType === 'number') {
                this.item.aggregation_type = 'default';
            } else {
                this.item.aggregation_type = 'none';
            }
            this.formatChanged();
        }
    }

    defaultConfig() {
        return this.tableUtils.getDefaultFormatConfig(this.options, this.default_values);
    }

    formatColumn(prop) {
        if (['bold', 'italic', 'abbreviate', 'resize', 'fit_content', 'underline', 'strikethrough'].includes(prop)) {
            this.item[prop] = !this.item[prop];
        }
        if ((prop === 'fit_content' || prop === 'resize') && !this.item[prop]) {
            this.item.width = null;
        }

        if (prop === 'colour' || prop === 'background_colour') {
            if (this.item.hasOwnProperty(prop) && this.item[prop]) {
                this.item[prop] = undefined;
            } else {
                this.item[prop] = 'black'; // initial colour
            }
        }
        if (!this.item['size']) {
            this.item['size'] = 100;
        }
        if (prop === 'plus') {
            if (this.item['size'] >= 150) {
                this.notification.openError('Upper font size limit reached', NOTIFICATION_DURATION_MS);
            } else {
                this.item['size'] = this.item['size'] + 10;
            }
        }
        if (prop === 'minus') {
            if (this.item['size'] <= 70) {
                this.notification.openError('Lower font size limit reached', NOTIFICATION_DURATION_MS);
            } else {
                this.item['size'] = this.item['size'] - 10;
            }

        }
        this.formatChanged();
    }

    formatChanged(): void {
        this.formatsChanged.next(this.item);
    }

    setAlignment(alignment) {
        if (this.item['align'] === alignment) {
            delete this.item['align'];
        } else {
            this.item['align'] = alignment;
        }
        this.formatsChanged.emit(this.item);
    }

    setDecimals(item, direction) {
        // Upper and lower limits
        if (!this.item['decimals']) {
            this.item['decimals'] = 0;
        }
        if (this.item['decimals'] === 0 && direction === -1 ||
            this.item['decimals'] === 10 && direction === 1) {
            this.notification.openError('Limit reached.', NOTIFICATION_DURATION_MS);
            return;
        }
        this.item['decimals'] += direction;
        this.formatsChanged.emit(this.item);
    }

    setAggregation($event, item, aggregation){
        $event.stopPropagation();
        item.aggregation_type = aggregation;
        this.formatChanged();
    }

}
